/**
 * Opening one row of a view in the row dialog.
 *
 * Every view type that shows rows can open one, and each needs the same four
 * answers: which row, where it came from if the window is not holding it,
 * whether there is another either side of it, and how to get there. Written
 * once here so the six of them cannot answer differently -- a row opened from a
 * gallery card is the same dialog, over the same row, as one opened from a grid.
 *
 * The row is read out of the window rather than copied when the dialog opened,
 * so an edit made behind it -- by this tab or another one -- shows through.
 */
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import type { Field, RowData } from '@/api/types'
import { fetchAdjacentRow } from '../row/requests'
import { useDetachedRow } from '../row/use-detached-row'
import { indexOfRow, type RowWindow } from '../rows/rowWindow'
import type { RowWindowMember } from '../rows/rowWindowRegistry'
import type { QueryableView, RowSearch } from '../rows/rowQuery'

/** Whether there is known to be a row either side of the open one. */
export interface RowSteps {
  previous: boolean
  next: boolean
}

/** Everything `RowModal` needs about which row it is showing. */
export interface OpenRow {
  member: RowWindowMember
  row: RowData | null
  missing: boolean
  steps: RowSteps
  onStep: (previous: boolean) => void
}

export function useRowDialog({
  window,
  count,
  member,
  view,
  fields,
  openRowId,
  onOpenRow,
  search,
}: {
  window: RowWindow
  count: number
  /** The view's own window, used whenever it is holding the row. */
  member: RowWindowMember
  view: QueryableView & { id: number; table_id: number }
  fields: readonly Field[]
  openRowId: number | null
  onOpenRow: (rowId: number | null) => void
  search: RowSearch
}): OpenRow {
  const { t } = useTranslation()

  const heldIndex = openRowId === null ? -1 : indexOfRow(window, openRowId)
  const heldRow =
    heldIndex === -1 ? undefined : window.rows[heldIndex - window.range.start]

  // A row the window is not holding -- a link somebody was sent, or a step past
  // the end of the buffer -- is fetched into a window of its own, so that
  // editing it goes down exactly the same path as editing one on screen.
  const detached = useDetachedRow({
    tableId: view.table_id,
    viewId: view.id,
    rowId: heldRow === undefined ? openRowId : null,
    fields,
    view,
  })

  /**
   * Known only for a row the window is holding, because only then is its place
   * in the view known. For one that was fetched by id the answer is "ask", and
   * the ask comes back empty at the ends.
   */
  const steps: RowSteps =
    heldIndex === -1
      ? { previous: true, next: true }
      : { previous: heldIndex > 0, next: heldIndex < count - 1 }

  function onStep(previous: boolean): void {
    if (openRowId === null) return
    void fetchAdjacentRow(view.table_id, openRowId, {
      viewId: view.id,
      previous,
      search,
    })
      .then((row) => {
        // Null is the end of the view rather than a failure, and staying put is
        // the right thing to do at it.
        if (row !== null) onOpenRow(row.id)
      })
      .catch(() => {
        toast.error(t('row.stepFailed'))
      })
  }

  return {
    // Everything the dialog does to a row goes through a member, so which
    // member it is is the whole of the difference between a row on screen and
    // one fetched by id.
    member: heldRow === undefined ? detached.member : member,
    row: heldRow ?? detached.row,
    missing: heldRow === undefined && detached.missing,
    steps,
    onStep,
  }
}
